import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import Checkbox from '@/components/Forms/Checkbox';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { FinanceAccountNumberResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormData = {
  name: string;
  value: string;
  important: boolean;
};

interface Props {
  accountNumber?: FinanceAccountNumberResource;
  onClose: () => void;
}

const AccountNumberFormModal: FC<Props> = ({ accountNumber, onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: accountNumber?.name || '',
      value: accountNumber?.value || '',
      important: accountNumber?.important || false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (accountNumber) {
          await api.put<typeof data, FinanceAccountNumberResource>(
            `/v1/finance-account-numbers/${accountNumber.id}`,
            data,
          );
        } else {
          await api.post<typeof data, FinanceAccountNumberResource>(
            '/v1/finance-account-numbers',
            data,
          );
        }

        await queryClient.invalidateQueries(['finance-account-numbers'], {
          exact: false,
        });

        onClose();
        alerts.success(
          `Account number was successfully ${accountNumber ? 'updated' : 'created'}`,
        );
      } catch (error) {
        handleFormErrors<FormData>(error, setError, alerts);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal>
      <SidePanel
        heading={`${accountNumber ? 'Edit' : 'Add'} Account Number`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormData>
                control={control}
                name="name"
                label="Name"
                id="nameInput"
                placeholder="Add account number name"
              />
              <FormField<FormData>
                control={control}
                name="value"
                label="Value"
                id="valueInput"
                placeholder="Add account number value"
              />
              <Larger>
                <Checkbox control={control} name="important">
                  Important
                </Checkbox>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {accountNumber ? 'Update' : 'Add'}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default AccountNumberFormModal;
